import React, { useMemo, useCallback } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';
import unitsData from '../../data/units_merged.json';

const IMAGE_BASE = '/assets/drawable/';
const IMAGES = ['zuluft_regelung_mit_erhitzer.PNG'];

type TextBlock = { heading: string; body: string };
const PAGE_ID = 'm1_u1_x1';
const M1_KEY = 'M1';
const U1_KEY = 'M1_U1';

const BASE_BLOCKS: Array<{ id: string; heading: string; body: string }> = [
  {
    id: 'block_lernziel',
    heading: 'Lernziel und Anlagenaufgabe',
    body: 'Die Zuluftregelung sorgt dafuer, dass ein Raum oder Anlagenbereich mit ausreichend temperierter und sicher bereitgestellter Luft versorgt wird. Ziel ist nicht nur eine feste Zulufttemperatur, sondern ein stabiler und betriebssicherer Anlagenzustand vom Einschalten bis zum Normalbetrieb.'
  },
  {
    id: 'block_anlagenaufbau',
    heading: 'Typischer Anlagenaufbau',
    body: 'Zur beschriebenen Zuluftanlage gehoeren in der Regel Aussenluftklappe, Filter, Heizregister mit Ventil, Umwaelzpumpe, Zuluftventilator, Temperaturfuehler und die zugehoerige DDC- oder GLT-Ansteuerung. Je nach Anlage kommen weitere Rueckmeldungen wie Ventilatorstoerung, Frostschutzwaechter oder Pumpenstatus hinzu.'
  },
  {
    id: 'block_regelziel',
    heading: 'Regelziel der Zuluftregelung',
    body: 'Die Regelung vergleicht den Istwert der Zulufttemperatur mit dem Sollwert und stellt ueber das Heizventil die benoetigte Waermeleistung ein. Die Zuluft soll weder zu kalt noch unkontrolliert zu warm in den Raum eingebracht werden. Gleichzeitig muessen Frostschutz, sichere Freigabe und Anlagenverfuegbarkeit jederzeit gewaehrleistet sein.'
  },
  {
    id: 'block_signalfluss',
    heading: 'Signalfluss im Betrieb',
    body: 'Der Start erfolgt ueblicherweise ueber Zeitprogramm oder Freigabebefehl. Danach prueft die Anfahrschaltung, ob Heizregister und hydraulische Seite betriebsbereit sind. Erst wenn Ruecklauftemperatur, Pumpenlauf und weitere Freigaben plausibel sind, werden Klappen und Ventilator freigegeben. Anschliessend arbeitet die Temperaturregelung im geschlossenen Kreis mit Fuehler, Regler und Stellglied.'
  },
  {
    id: 'block_anfahrschaltung',
    heading: 'Anfahrschaltung Schritt fuer Schritt',
    body: 'Vor dem Anlauf oeffnet die Regelung typischerweise das Heizventil und startet die Umwaelzpumpe. Damit wird sichergestellt, dass das Register durchstroemt und keine kalte Anlage angefahren wird. Erst nach erfolgreicher Vorwaermung und Rueckmeldung startet der Zuluftventilator. Diese Reihenfolge verhindert Kaltlufteintrag und reduziert das Risiko eines Registerfrosts.'
  },
  {
    id: 'block_sensorik',
    heading: 'Sensoren, Aktoren und Rueckmeldungen',
    body: 'Wichtige Signale sind Zulufttemperatur, Ruecklauftemperatur des Heizmediums, Frostschutzkontakt, Pumpenfreigabe und gegebenenfalls Ventilatorrueckmeldung. Das Heizventil ist das zentrale Stellglied fuer die Temperaturregelung. Klappen, Pumpe und Ventilator gehoeren zur Freigabe- und Schutzkette und muessen in der Inbetriebnahme einzeln geprueft werden.'
  },
  {
    id: 'block_schutz',
    heading: 'Frostschutz und Stillstandsregelung',
    body: 'Der Frostschutz schuetzt das Heizregister vor dem Einfrieren. Bei kritischen Temperaturen oeffnet das Heizventil, die Pumpe wird eingeschaltet und im Ernstfall wird die Anlage abgeschaltet. Die Stillstandsregelung haelt bei ausgeschalteter Anlage eine sichere Mindesttemperatur oder eine schutzwirksame Ventilstellung, damit Register, Rohrleitung und waermefuehrende Bauteile nicht beschaedigt werden.'
  },
  {
    id: 'block_stoerungen',
    heading: 'Typische Stoerungen und Pruefpunkte',
    body: 'Wenn keine Freigabe erfolgt, zuerst Zeitprogramm, Betriebsart und Stoermeldungen pruefen. Bei zu kalter Zuluft Heizventilstellung, Pumpenlauf, Ruecklauftemperatur und Fuehlerwerte vergleichen. Bei Frostalarm den Frostschutzwaechter, Ventilhub und die hydraulische Versorgung kontrollieren. In der Praxis ist entscheidend, ob das Problem aus der Freigabekette, der Hydraulik oder aus einer falschen Messwertlage stammt.'
  },
  {
    id: 'block_praxis',
    heading: 'Praxisblick fuer Inbetriebnahme und Service',
    body: 'Bei der Inbetriebnahme sollte die Reihenfolge Freigabe, Ventil auf, Pumpe an, Vorwaermung, Ventilatorstart und anschliessende Temperaturregelung nachvollziehbar getestet werden. Im Servicefall hilft eine einfache Fragefolge: Liegt eine Freigabe an? Wird Waerme bereitgestellt? Ist der Temperaturfuehler plausibel? Reagiert das Heizventil? Greift ein Schutzprogramm ein? Genau diese Kette trennt systematisch Regelungsfehler von Anlagen- oder Feldproblemen.'
  }
];

function extractSection(fullText: string, startMarker: string, endMarker?: string) {
  if (!fullText) return '';
  const start = fullText.indexOf(startMarker);
  if (start === -1) return '';
  const from = start + startMarker.length;
  if (!endMarker) return fullText.substring(from).trim();
  const end = fullText.indexOf(endMarker, from);
  if (end === -1) return fullText.substring(from).trim();
  return fullText.substring(from, end).trim();
}

function normalizeParagraphs(section: string): string[] {
  return section
    .split('\n\n')
    .map((paragraph) => paragraph.replace(/\\n/g, '\n').trim())
    .filter(Boolean);
}

function mergeBaseWithSection(section: string): Array<{ id: string; heading: string; body: string }> {
  const paragraphs = normalizeParagraphs(section);
  const paragraphQueue = [...paragraphs];

  const consumeParagraph = (label: string) => {
    const idx = paragraphQueue.findIndex((paragraph) => paragraph.toLowerCase().startsWith(label.toLowerCase()));
    if (idx === -1) return '';
    const [value] = paragraphQueue.splice(idx, 1);
    return value.replace(new RegExp(`^${label}:?\\s*`, 'i'), '').trim();
  };

  const anlagenelemente = consumeParagraph('Anlagenelemente');
  const regelung = consumeParagraph('Regelung');
  const funktionsbeschreibung = consumeParagraph('Funktionsbeschreibung');
  const beschreibungsBloecke = funktionsbeschreibung
    ? funktionsbeschreibung.split('\n\n').map((block) => block.trim()).filter(Boolean)
    : [];

  return BASE_BLOCKS.map((block, index) => {
    let mergedBody = block.body;

    if (block.id === 'block_anlagenaufbau' && anlagenelemente) {
      mergedBody = `${anlagenelemente}\n\n${block.body}`;
    } else if (block.id === 'block_regelziel' && regelung) {
      mergedBody = `${regelung}\n\n${block.body}`;
    } else if (beschreibungsBloecke[index - 3]) {
      mergedBody = `${beschreibungsBloecke[index - 3]}\n\n${block.body}`;
    }

    return {
      ...block,
      body: mergedBody
    };
  });
}

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM1, canUploadImagesM1 } = useAuth();
  
  // Lade Overrides
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const unit = ((unitsData as any)[M1_KEY] || {})[U1_KEY] || null;
  const fullText: string = unit?.text || '';
  const section = extractSection(fullText, '-- X1_Zuluftregelung --', '-- X2_Abluftkaskade --');
  const sourceBlocks = useMemo(() => mergeBaseWithSection(section), [section]);

  const title = '1.1.1 Zuluftregelung';

  const structured = useMemo<TextBlock[]>(() => {
    return sourceBlocks.map((block) => ({
      heading: block.heading,
      body: override?.texts?.[block.id] || block.body
    }));
  }, [override, sourceBlocks]);

  const blockIds = useMemo(() => sourceBlocks.map((block) => block.id), [sourceBlocks]);

  const fullPlainText = useMemo(() => 
    structured.map(b => `${b.heading ? b.heading + '. ' : ''}${b.body}`).join(' '), 
    [structured]
  );

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const getImageSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    let s = String(v).trim();
    // Entferne Pfad-Präfixe falls vorhanden
    if (s.includes('/')) {
      s = s.split('/').pop() || s;
    }
    // Stelle sicher, dass nur Filename verbleibt
    return `/assets/unit_custom/${s}`;
  };

  const handleTextSave = useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canEditTextsM1) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      console.error('Text-Speichern fehlgeschlagen:', e);
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTextsM1]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canUploadImagesM1) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      console.error('Bild-Upload fehlgeschlagen:', e);
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImagesM1]);

  // TTS Button Action
  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title, actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        IMAGES.map((img, idx) => 
          React.createElement(ZoomableImage, {
            key: img,
            src: getImageSrc(`img_${idx}`, `${IMAGE_BASE}${img}`),
            alt: img,
            label: 'Bild einfügen',
            onReplace: canUploadImagesM1 ? (file: File) => handleImageReplace(`img_${idx}`, file) : undefined
          })
        )
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        structured.map((block, i) =>
          React.createElement(EditableTextBlock, {
            key: i,
            pageId: PAGE_ID,
            blockId: blockIds[i],
            heading: block.heading,
            body: block.body,
            onSave: (newBody: string) => handleTextSave(blockIds[i], newBody)
          })
        ),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m1_u1_x99_quiz') }, 'Zum Quiz')
        )
      )
    )
  );
};

export default Page;


